'use client';

import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { useGlobalModal } from '@/app/hooks';
import {
  compressImageIfNeeded,
  type ImagePrepareFailureReason,
  type ImageValidationReason,
  validateImageFile,
} from '@/app/utils/client';
import { normalizePath } from '../_utils';
import { truncateUploadErrorList } from './upload-error-toast-format';
import { emitImageCompressFailedToastWith, emitImageRejectedToastWith } from './use-claw-image-toasts';
import { useClawInactiveHandler } from './use-claw-inactive-handler';

const truncateList = truncateUploadErrorList;

/**
 * 根据冲突文件名生成下一个候选名（实现已抽到 ./generate-next-suggested-name 以便独立单测）。
 */
import { generateNextSuggestedName } from './generate-next-suggested-name';

export { generateNextSuggestedName };

/**
 * 单文件上传的标准化结果。
 * - `ok: true` 表示该文件已经成功上传
 * - `conflict: true` 表示文件名与已有文件冲突，可以触发改名重传
 * - `invalidName: true` 表示文件名/类型在服务端被拒绝，提示用户改名
 * - `clawInactive: true` 表示 claw 已失效，整个批次终止
 */
export interface UploadOnceOutcome<TData = unknown> {
  ok: boolean;
  conflict?: boolean;
  invalidName?: boolean;
  clawInactive?: boolean;
  moderateRejected?: boolean;
  data?: TData;
}

export interface UploadFileEntry<TMeta = unknown> {
  file: File;
  meta: TMeta;
}

export interface UploadWithRenameOnConflictOptions<TData = unknown, TMeta = undefined> {
  files?: File[];
  fileEntries?: UploadFileEntry<TMeta>[];
  targetFolderPath?: string;
  isAllowedFile?: (file: File) => boolean;
  /**
   * 单文件上传执行函数。在初次批量上传与改名后的重传中都会被调用。
   * - 必须把服务端返回的"冲突 / 失活 / 名称非法"等状态归一为 {@link UploadOnceOutcome}
   * - 由调用方决定底层是 `useClawFolder.uploadFiles` 还是 `useUploadClawFile`
   */
  uploadOnce: (file: File, meta: TMeta | undefined) => Promise<UploadOnceOutcome<TData>>;
  /**
   * 单文件上传成功后的副作用回调。初次上传与改名后的重传都会触发。
   * 用来处理外部的 preview_url 注入、草稿列表更新等定制行为。
   */
  onSuccess?: (uploadedFile: File, data: TData | undefined, meta: TMeta | undefined) => void;
}

export interface UploadWithRenameOnConflictResult {
  /** 成功上传的总数（含原始批次成功 + 改名后成功） */
  successCount: number;
  /** 文件名/类型不被允许的文件名列表（首次批次过滤） */
  invalidFileNameList: string[];
  /** 用户主动取消的文件名列表 */
  cancelledFileNameList: string[];
  /** 上传失败（非冲突）的文件名列表 */
  failFileNameList: string[];
  /** Claw 是否在过程中失效 */
  clawInactive: boolean;
  /** 图片校验失败的文件（按入参顺序），含拒因，统一走聚合 toast */
  imageRejectedList: Array<{ name: string; reason: ImageValidationReason }>;
  /** 图片在压缩阶段失败的文件，含拒因，统一走聚合 toast */
  imageCompressFailedList: Array<{ name: string; reason: ImagePrepareFailureReason }>;
  /** 内容审核未通过的文件名列表，统一走聚合 toast */
  moderateRejectedList: string[];
}

/**
 * 上传文件并自动处理同名冲突的统一封装。
 * - 通过外部传入的 `uploadOnce` 完成单文件上传，hook 不耦合具体的请求实现
 * - 初次批量上传后，对返回冲突的文件依次串行弹出 RenameOnConflictDialog
 * - 用户改名后再次调用 `uploadOnce` 重传
 * - 改名后仍冲突会在弹窗内行内报错并允许继续修改
 * - 全部处理完毕后统一 toast（成功 / 失败 / 取消计数）
 */
export const useUploadWithRenameOnConflict = () => {
  const t = useTranslations();
  const { handleClawInactiveError } = useClawInactiveHandler();
  const renameDialog = useGlobalModal('renameOnConflictDialog');

  const uploadWithRenameOnConflict = useCallback(
    async <TData = unknown, TMeta = undefined>({
      files,
      fileEntries,
      targetFolderPath,
      isAllowedFile,
      uploadOnce,
      onSuccess,
    }: UploadWithRenameOnConflictOptions<TData, TMeta>): Promise<UploadWithRenameOnConflictResult> => {
      const normalizedTargetPath = normalizePath(targetFolderPath);
      const aggregate: UploadWithRenameOnConflictResult = {
        successCount: 0,
        invalidFileNameList: [],
        cancelledFileNameList: [],
        failFileNameList: [],
        clawInactive: false,
        imageRejectedList: [],
        imageCompressFailedList: [],
        moderateRejectedList: [],
      };

      const uploadEntries: UploadFileEntry<TMeta | undefined>[] =
        fileEntries ?? files?.map((file) => ({ file, meta: undefined })) ?? [];
      const validEntries: UploadFileEntry<TMeta | undefined>[] = [];
      uploadEntries.forEach((entry) => {
        if (isAllowedFile && !isAllowedFile(entry.file)) {
          aggregate.invalidFileNameList.push(entry.file.name);
        } else {
          validEntries.push(entry);
        }
      });

      // 图片在白名单 MIME 通过后，再走一层与工具侧一致的校验：
      // magic byte / 大小 / 面积 / 比例 / 解码失败。命中拒因的图片不发起任何 uploadOnce，
      // 统一进 imageRejectedList，由 emitBatchToast 合到聚合 toast 里。
      // 校验通过的图片，再走与工具侧一致的"按需压缩"：
      // > 10MB 或面积 > 6000×6000 → resize 到限制面积内 + 转 webp，传给后端的是压缩后的 File。
      // 压缩失败的图片不发起 uploadOnce，进 imageCompressFailedList。
      const passedEntries: UploadFileEntry<TMeta | undefined>[] = [];
      for (const entry of validEntries) {
        const { file } = entry;
        if (!file.type.startsWith('image/')) {
          passedEntries.push(entry);
          continue;
        }
        // eslint-disable-next-line no-await-in-loop
        const validation = await validateImageFile(file);
        if (!validation.valid) {
          aggregate.imageRejectedList.push({ name: file.name, reason: validation.reason });
          continue;
        }
        // claw 不复用工具侧 imageURL，立即释放避免内存堆积
        URL.revokeObjectURL(validation.imageURL);

        // eslint-disable-next-line no-await-in-loop
        const prepared = await compressImageIfNeeded(file, {
          width: validation.imageResolution.width,
          height: validation.imageResolution.height,
        });
        if (!prepared.ok) {
          aggregate.imageCompressFailedList.push({ name: file.name, reason: prepared.reason });
          continue;
        }
        passedEntries.push({ ...entry, file: prepared.file });
      }

      if (!passedEntries.length) {
        emitBatchToast(aggregate, t);
        return aggregate;
      }

      const initialResults = await Promise.allSettled(passedEntries.map((entry) => uploadOnce(entry.file, entry.meta)));

      const knownTakenNames = new Set<string>();
      const conflictEntries: UploadFileEntry<TMeta | undefined>[] = [];
      const seenConflictNames = new Set<string>();

      initialResults.forEach((settled, index) => {
        const entry = passedEntries[index];
        if (!entry) return;
        const { file } = entry;

        if (settled.status === 'rejected') {
          aggregate.failFileNameList.push(file.name);
          return;
        }

        const outcome = settled.value;
        if (outcome.ok) {
          aggregate.successCount += 1;
          knownTakenNames.add(file.name);
          onSuccess?.(file, outcome.data, entry.meta);
          return;
        }
        if (outcome.clawInactive) {
          aggregate.clawInactive = true;
          return;
        }
        if (outcome.moderateRejected) {
          aggregate.moderateRejectedList.push(file.name);
          return;
        }
        if (outcome.conflict) {
          knownTakenNames.add(file.name);
          if (!seenConflictNames.has(file.name)) {
            seenConflictNames.add(file.name);
            conflictEntries.push(entry);
          }
          return;
        }
        aggregate.failFileNameList.push(file.name);
      });

      if (aggregate.clawInactive) {
        handleClawInactiveError({ reason: 'claw_is_inactive' });
        emitBatchToast(aggregate, t);
        return aggregate;
      }

      for (const conflictEntry of conflictEntries) {
        if (aggregate.clawInactive) {
          aggregate.cancelledFileNameList.push(conflictEntry.file.name);
          continue;
        }
        // eslint-disable-next-line no-await-in-loop
        const outcome = await resolveSingleConflict<TData, TMeta>({
          file: conflictEntry.file,
          meta: conflictEntry.meta,
          targetFolderPath: normalizedTargetPath,
          knownTakenNames,
          uploadOnce,
          onSuccess,
          renameDialog,
          stillConflictMessage: t('claw.upload.renameOnConflict.stillConflict'),
          invalidNameMessage: t('claw.upload.renameOnConflict.invalidName'),
        });

        if (outcome.kind === 'clawInactive') {
          aggregate.clawInactive = true;
          aggregate.cancelledFileNameList.push(conflictEntry.file.name);
          handleClawInactiveError({ reason: 'claw_is_inactive' });
          continue;
        }

        if (outcome.kind === 'success') {
          aggregate.successCount += 1;
          knownTakenNames.add(outcome.uploadedName);
        } else if (outcome.kind === 'cancelled') {
          aggregate.cancelledFileNameList.push(conflictEntry.file.name);
        } else if (outcome.kind === 'moderateRejected') {
          aggregate.moderateRejectedList.push(outcome.fileName);
        } else if (outcome.kind === 'failed') {
          aggregate.failFileNameList.push(outcome.fileName);
        }
      }

      emitBatchToast(aggregate, t);
      return aggregate;
    },
    [renameDialog, handleClawInactiveError, t],
  );

  return { uploadWithRenameOnConflict };
};

type ResolveSingleConflictOptions<TData, TMeta> = {
  file: File;
  meta: TMeta | undefined;
  targetFolderPath: string;
  knownTakenNames: Set<string>;
  uploadOnce: (file: File, meta: TMeta | undefined) => Promise<UploadOnceOutcome<TData>>;
  onSuccess?: (uploadedFile: File, data: TData | undefined, meta: TMeta | undefined) => void;
  renameDialog: ReturnType<typeof useGlobalModal<'renameOnConflictDialog'>>;
  stillConflictMessage: string;
  invalidNameMessage: string;
};

type ResolveSingleConflictOutcome =
  | { kind: 'success'; uploadedName: string }
  | { kind: 'cancelled' }
  | { kind: 'failed'; fileName: string }
  | { kind: 'clawInactive' }
  | { kind: 'moderateRejected'; fileName: string };

const resolveSingleConflict = async <TData, TMeta>({
  file,
  meta,
  targetFolderPath,
  knownTakenNames,
  uploadOnce,
  onSuccess,
  renameDialog,
  stillConflictMessage,
  invalidNameMessage,
}: ResolveSingleConflictOptions<TData, TMeta>): Promise<ResolveSingleConflictOutcome> =>
  new Promise((resolve) => {
    const settle = (outcome: ResolveSingleConflictOutcome): void => {
      renameDialog.close();
      resolve(outcome);
    };

    const handleConfirm = async (newName: string): Promise<void> => {
      const trimmed = newName.trim();
      if (!trimmed) {
        renameDialog.updateState({ errorMessage: invalidNameMessage, submitting: false });
        return;
      }

      renameDialog.updateState({ errorMessage: undefined, submitting: true });

      const renamedFile = new File([file], trimmed, {
        type: file.type,
        lastModified: file.lastModified,
      });
      const result = await uploadOnce(renamedFile, meta);

      if (result.clawInactive) {
        settle({ kind: 'clawInactive' });
        return;
      }

      if (result.ok) {
        onSuccess?.(renamedFile, result.data, meta);
        settle({ kind: 'success', uploadedName: trimmed });
        return;
      }

      if (result.conflict) {
        knownTakenNames.add(trimmed);
        const nextSuggestion = generateNextSuggestedName(file.name, knownTakenNames);
        renameDialog.updateState({
          defaultSuggestedName: nextSuggestion,
          errorMessage: stillConflictMessage,
          submitting: false,
        });
        return;
      }

      if (result.invalidName) {
        renameDialog.updateState({ errorMessage: invalidNameMessage, submitting: false });
        return;
      }

      if (result.moderateRejected) {
        settle({ kind: 'moderateRejected', fileName: trimmed });
        return;
      }

      // 其它失败情况，不再骚扰用户，直接关闭弹窗并计入失败
      settle({ kind: 'failed', fileName: trimmed });
    };

    const handleCancel = (): void => {
      settle({ kind: 'cancelled' });
    };

    const initialSuggestion = generateNextSuggestedName(file.name, knownTakenNames);
    renameDialog.open({
      originalName: file.name,
      currentPath: targetFolderPath,
      defaultSuggestedName: initialSuggestion,
      submitting: false,
      errorMessage: undefined,
      onConfirm: handleConfirm,
      onCancel: handleCancel,
    });
  });

const emitBatchToast = (aggregate: UploadWithRenameOnConflictResult, t: ReturnType<typeof useTranslations>): void => {
  const {
    successCount,
    invalidFileNameList,
    cancelledFileNameList,
    failFileNameList,
    clawInactive,
    imageRejectedList,
    imageCompressFailedList,
    moderateRejectedList,
  } = aggregate;

  if (invalidFileNameList.length > 0) {
    toast({
      type: 'error',
      message: `${t('claw.file.uploadUnsupported')}${truncateList(invalidFileNameList)}`,
    });
  }

  if (imageRejectedList.length > 0) {
    emitImageRejectedToastWith(t, imageRejectedList);
  }

  if (imageCompressFailedList.length > 0) {
    emitImageCompressFailedToastWith(
      t,
      imageCompressFailedList.map((item) => item.name),
    );
  }

  if (moderateRejectedList.length > 0) {
    toast({
      type: 'error',
      message: `${t('claw.upload.moderateRejected')}${truncateList(moderateRejectedList)}`,
    });
  }

  if (clawInactive) {
    // claw 失效由上层 inactive handler 提示，这里不再重复 toast
    return;
  }

  if (successCount > 0) {
    toast({
      type: 'success',
      message: t('claw.upload.renameOnConflict.batchSuccess', { count: successCount }),
    });
  }

  if (cancelledFileNameList.length > 0) {
    toast({
      type: 'info',
      message: `${t('claw.upload.renameOnConflict.batchCancelled', {
        count: cancelledFileNameList.length,
      })}${truncateList(cancelledFileNameList)}`,
    });
  }

  if (failFileNameList.length > 0) {
    toast({
      type: 'error',
      message: `${t('claw.file.uploadFailed')}${truncateList(failFileNameList)}`,
    });
  }
};
